import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const STATUS_FLOW = ['open', 'in-progress', 'won', 'lost'];
const STATUS_LABELS = { open: 'Open', 'in-progress': 'In Progress', won: 'Won', lost: 'Lost' };
const STATUS_COLORS = {
  open: 'bg-primary/10 text-primary border-primary/20',
  'in-progress': 'bg-warning/10 text-warning border-warning/20',
  won: 'bg-success/10 text-success border-success/20',
  lost: 'bg-error/10 text-error border-error/20',
};

const GrowthOpportunityPanel = ({ opportunities, onCreateOpportunity, onUpdateOpportunity, onNavigateToEngagement }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const displayOpps = opportunities?.length > 0 ? opportunities : [];

  // AI scoring: rank by (confidence * potentialRevenue) / timeframe_months
  const getTimeframeMonths = (tf) => {
    if (!tf) return 12;
    const match = tf?.match(/(\d+)/);
    return match ? parseInt(match?.[1]) : 12;
  };

  const getAIScore = (opp) => {
    const rev = opp?.potential_revenue || opp?.potentialRevenue || 0;
    const conf = opp?.confidence || 50;
    const months = getTimeframeMonths(opp?.timeframe);
    return (conf * rev) / (months * 1000);
  };

  const topOppId = displayOpps?.length > 0
    ? displayOpps?.reduce((best, opp) => getAIScore(opp) > getAIScore(best) ? opp : best, displayOpps?.[0])?.id
    : null;

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'market-expansion': return 'Globe';
      case 'solution-bundling': return 'Package';
      case 'service-expansion': return 'Settings';
      case 'geographic-expansion': return 'MapPin';
      default: return 'TrendingUp';
    }
  };

  const getCategoryLabel = (category) => {
    return category?.split('-')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-error/10 text-error border-error/20';
      case 'medium': return 'bg-warning/10 text-warning border-warning/20';
      case 'low': return 'bg-primary/10 text-primary border-primary/20';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'text-success';
    if (confidence >= 60) return 'text-primary';
    return 'text-warning';
  };

  const handleStatusChange = async (opp, newStatus) => {
    if (!onUpdateOpportunity) return;
    setUpdatingId(opp?.id);
    try {
      await onUpdateOpportunity(opp?.id, { status: newStatus });
    } finally {
      setUpdatingId(null);
    }
  };

  const totalRevenue = displayOpps?.reduce((sum, opp) => sum + (opp?.potential_revenue || opp?.potentialRevenue || 0), 0);
  const highPriority = displayOpps?.filter(opp => opp?.priority === 'high')?.length;
  const openCount = displayOpps?.filter(opp => !opp?.status || opp?.status === 'open')?.length;
  const inProgressCount = displayOpps?.filter(opp => opp?.status === 'in-progress')?.length;

  return (
    <div>
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-12 mb-24">
        <div className="p-16 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center gap-12 mb-8">
            <Icon name="TrendingUp" size={18} className="text-primary" />
            <span className="text-sm text-muted-foreground caption">Total</span>
          </div>
          <div className="text-2xl font-bold text-foreground data-text">{displayOpps?.length}</div>
        </div>
        <div className="p-16 bg-success/5 rounded-lg border border-success/20">
          <div className="flex items-center gap-12 mb-8">
            <Icon name="DollarSign" size={18} className="text-success" />
            <span className="text-sm text-muted-foreground caption">Pipeline</span>
          </div>
          <div className="text-2xl font-bold text-success data-text">
            OMR {(totalRevenue / 1000000)?.toFixed(1)}M
          </div>
        </div>
        <div className="p-16 bg-error/5 rounded-lg border border-error/20">
          <div className="flex items-center gap-12 mb-8">
            <Icon name="AlertCircle" size={18} className="text-error" />
            <span className="text-sm text-muted-foreground caption">High Priority</span>
          </div>
          <div className="text-2xl font-bold text-error data-text">{highPriority}</div>
        </div>
        <div className="p-16 bg-warning/5 rounded-lg border border-warning/20">
          <div className="flex items-center gap-12 mb-8">
            <Icon name="Clock" size={18} className="text-warning" />
            <span className="text-sm text-muted-foreground caption">In Progress</span>
          </div>
          <div className="text-2xl font-bold text-warning data-text">{inProgressCount}</div>
        </div>
      </div>

      {/* Header row */}
      <div className="flex items-center justify-between mb-16">
        <h3 className="text-base font-semibold text-foreground">All Opportunities</h3>
        <Button variant="default" iconName="Plus" iconPosition="left" iconSize={16} onClick={() => onCreateOpportunity?.()}>
          Add Opportunity
        </Button>
      </div>

      {displayOpps?.length === 0 ? (
        <div className="text-center py-32 text-muted-foreground">
          <Icon name="Lightbulb" size={40} className="mx-auto mb-12 opacity-30" />
          <p className="text-sm">No growth opportunities yet. Add one to get started.</p>
        </div>
      ) : (
        <div className="space-y-12">
          {displayOpps?.map((opportunity) => {
            const isTop = opportunity?.id === topOppId;
            const currentStatus = opportunity?.status || 'open';
            return (
              <div
                key={opportunity?.id}
                className={`border rounded-lg overflow-hidden hover:border-primary/50 transition-smooth ${isTop ? 'border-amber-300 ring-1 ring-amber-200' : 'border-border'}`}
              >
                <div
                  className="p-16 bg-muted/30 cursor-pointer"
                  onClick={() => setExpandedId(expandedId === opportunity?.id ? null : opportunity?.id)}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-16">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-12 mb-12">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name={getCategoryIcon(opportunity?.category)} size={20} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-8 flex-wrap mb-4">
                            <h3 className="text-base font-semibold text-foreground">{opportunity?.title}</h3>
                            {isTop && (
                              <span className="flex items-center gap-4 px-8 py-3 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold border border-amber-300">
                                <Icon name="Sparkles" size={12} />
                                AI Recommended
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-8">
                            <span className={`px-8 py-4 rounded-md text-xs font-medium border ${getPriorityColor(opportunity?.priority)}`}>
                              {opportunity?.priority?.toUpperCase()} PRIORITY
                            </span>
                            <span className="text-xs text-muted-foreground caption">
                              {getCategoryLabel(opportunity?.category)}
                            </span>
                            {/* Status badge */}
                            <span className={`px-8 py-4 rounded-md text-xs font-medium border ${STATUS_COLORS?.[currentStatus] || STATUS_COLORS?.open}`}>
                              {STATUS_LABELS?.[currentStatus] || currentStatus}
                            </span>
                          </div>
                        </div>
                      </div>

                      {opportunity?.description && (
                        <p className="text-sm text-muted-foreground mb-12">{opportunity?.description}</p>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
                        <div>
                          <div className="text-xs text-muted-foreground caption mb-4">Potential Revenue</div>
                          <div className="text-sm font-semibold text-success data-text">
                            OMR {((opportunity?.potential_revenue || opportunity?.potentialRevenue || 0) / 1000000)?.toFixed(1)}M
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground caption mb-4">Timeframe</div>
                          <div className="text-sm font-medium text-foreground">{opportunity?.timeframe || opportunity?.target_quarter || '—'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground caption mb-4">Confidence</div>
                          <div className={`text-sm font-semibold data-text ${getConfidenceColor(opportunity?.confidence || 50)}`}>
                            {opportunity?.confidence || 50}%
                          </div>
                        </div>
                        <div className="flex items-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName={expandedId === opportunity?.id ? 'ChevronUp' : 'ChevronDown'}
                            iconPosition="right"
                            iconSize={16}
                          >
                            {expandedId === opportunity?.id ? 'Less' : 'More'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {expandedId === opportunity?.id && (
                  <div className="p-16 border-t border-border bg-card">
                    {/* Status Workflow */}
                    <div className="mb-16 p-12 bg-muted/30 rounded-lg border border-border">
                      <div className="text-xs font-semibold text-muted-foreground mb-10">Status Workflow</div>
                      <div className="flex items-center gap-8 flex-wrap">
                        {STATUS_FLOW?.map((s, idx) => (
                          <React.Fragment key={s}>
                            <button
                              onClick={(e) => { e?.stopPropagation(); handleStatusChange(opportunity, s); }}
                              disabled={updatingId === opportunity?.id}
                              className={`px-12 py-6 rounded-lg text-xs font-semibold border transition-all ${
                                currentStatus === s
                                  ? STATUS_COLORS?.[s] + 'ring-2 ring-offset-1 ring-current' :'bg-muted/50 text-muted-foreground border-border hover:border-primary/40'
                              }`}
                            >
                              {updatingId === opportunity?.id && currentStatus !== s ? STATUS_LABELS?.[s] : STATUS_LABELS?.[s]}
                            </button>
                            {idx < STATUS_FLOW?.length - 1 && (
                              <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
                      {opportunity?.requirements?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-12 flex items-center gap-8">
                            <Icon name="CheckSquare" size={16} className="text-primary" />
                            Requirements
                          </h4>
                          <ul className="space-y-8">
                            {opportunity?.requirements?.map((req, index) => (
                              <li key={index} className="flex items-start gap-8 text-sm text-muted-foreground">
                                <Icon name="Circle" size={8} className="flex-shrink-0 mt-6" />
                                {req}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {opportunity?.nextSteps?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-12 flex items-center gap-8">
                            <Icon name="ArrowRight" size={16} className="text-primary" />
                            Next Steps
                          </h4>
                          <ul className="space-y-8">
                            {opportunity?.nextSteps?.map((step, index) => (
                              <li key={index} className="flex items-start gap-8 text-sm text-muted-foreground">
                                <Icon name="Circle" size={8} className="flex-shrink-0 mt-6" />
                                {step}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {opportunity?.risks?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-12 flex items-center gap-8">
                            <Icon name="AlertTriangle" size={16} className="text-warning" />
                            Risks
                          </h4>
                          <ul className="space-y-8">
                            {opportunity?.risks?.map((risk, index) => (
                              <li key={index} className="flex items-start gap-8 text-sm text-muted-foreground">
                                <Icon name="Circle" size={8} className="flex-shrink-0 mt-6" />
                                {risk}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    <div className="mt-16 pt-16 border-t border-border flex items-center justify-between gap-12">
                      <button
                        onClick={() => onNavigateToEngagement?.()}
                        className="flex items-center gap-8 text-sm text-primary hover:underline font-medium"
                      >
                        <Icon name="ExternalLink" size={14} />
                        Create Engagement in Hub
                      </button>
                      <div className="flex items-center gap-12">
                        <Button variant="outline" size="sm" onClick={() => setExpandedId(null)}>
                          Close
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          iconName="ArrowRight"
                          iconPosition="right"
                          iconSize={16}
                          onClick={() => onNavigateToEngagement?.()}
                        >
                          Create Action Plan
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GrowthOpportunityPanel;